import type React from "react";
import { createContext, type ReactNode, use, useEffect, useMemo, useState } from "react";
import { useDatasources } from "src/hooks/useDatasources";
import { updateSettings } from "../db";

interface TabContextType {
    selectedTab: string;
    setSelectedTab: (tab: string) => void;
    selectedSearchTab: string;
    setSelectedSearchTab: (tab: string) => void;
}

const defaultContextValue: TabContextType = {
    selectedTab: "combined",
    setSelectedTab: () => {},
    selectedSearchTab: "All",
    setSelectedSearchTab: () => {},
};

const TabContext = createContext(defaultContextValue);

interface TabProviderProps {
    children: ReactNode;
}

export const TabProvider: React.FC<TabProviderProps> = ({ children }) => {
    const [selectedTab, setSelectedTab] = useState<string>(() => {
        return "combined";
    });
    const [selectedSearchTab, setSelectedSearchTab] = useState<string>(() => {
        const preferences = localStorage.getItem("chatPreferences");
        return preferences ? JSON.parse(preferences).searchTab : "All";
    });
    const { data: chatVectorIndex = [], isLoading } = useDatasources();

    useEffect(() => {
        localStorage.setItem("selectedTab", selectedTab);
        updateSettings("chat_vector_index", selectedTab);
    }, [selectedTab]);

    useEffect(() => {
        localStorage.setItem("selectedSearchTab", selectedSearchTab);
    }, [selectedSearchTab]);

    const value = useMemo(
        () => ({
            selectedTab,
            setSelectedTab,
            selectedSearchTab,
            setSelectedSearchTab,
        }),
        [selectedTab, selectedSearchTab],
    );

    const isPlaywright = window.location.search.includes("isPlaywright=true");

    if (isLoading && chatVectorIndex.length === 0 && !isPlaywright) {
        return null; // Or return a loading spinner here if preferred
    }

    return <TabContext value={value}>{children}</TabContext>;
};

export const useTab = () => use(TabContext);
